'use client';

import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

const REVIEWS = [
  { text: '마음이 따뜻해지는 경험이었습니다', author: '박*진' },
  { text: '기부도 하고 답례품도 받고 마음도 뿌듯해요!', author: '박*암' },
  { text: '손해볼거없이 좋기만한 선택', author: '정*인' },
  {
    text: '쉽고 편하게 기부하고 답례품도 받을 수 있어서 좋아요!',
    author: '원*아',
  },
  { text: '일단 해보고 생각하자', author: '장*원' },
  { text: '세액공제에 선물까지 주는데 Go!', author: '김*희' },
  {
    text: '각 지역을 대표하는 특산품도 경험할 수 있는 좋은 기회',
    author: '조*심',
  },
  { text: '마음이 따뜻해지는 경험', author: '박*운' },
  { text: '일석이조의 기쁨이었어요', author: '박*예' },
  { text: '혜택도 마음도 두배로 얻을수있어요', author: '이*혜' },
  { text: '답례품도 받고 기부도 하고 기분좋아요', author: '박*수' },
  { text: '기부도 하고 훌륭한 답례품도 받고 세액 공제까지!', author: '홍*숙' },
  { text: '작지만 간편한 방법으로 뿌듯해지는 경험', author: '이*진' },
  { text: '세액공제도 되고 지역 특산품도 받아서 일석이조!', author: '허*승' },
  { text: '기부를 간편하게 할 수 있어요', author: '이*선' },
  { text: '고향을 위한다는 좋은 느낌과 좋은 답례품', author: '남*우' },
  { text: '세액공제만큼은 필수로 합시다', author: '고*진' },
  { text: '믿고 기부하자', author: '연*주' },
  {
    text: '가보지 못한 지역도 작게나마 응원할 수 있어 좋습니다',
    author: '권*은',
  },
  { text: '기부와 혜택 모두 누릴 수 있는 뿌듯한 경험', author: '김*영' },
] as const;

const ANIMATION_CONFIG = {
  carouselRotationIntervalMs: 1500,
  transitionDurationSeconds: 0.3,
  easingCurve: [0.4, 0.0, 0.2, 1] as [number, number, number, number],
} as const;

export const ReviewSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const carouselInterval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % REVIEWS.length);
    }, ANIMATION_CONFIG.carouselRotationIntervalMs);

    return () => clearInterval(carouselInterval);
  }, []);

  const currentReview = REVIEWS[currentIndex];
  const nextReview = REVIEWS[(currentIndex + 1) % REVIEWS.length];
  const thirdReview = REVIEWS[(currentIndex + 2) % REVIEWS.length];

  return (
    <section
      id="review"
      className="flex w-full flex-col items-center gap-32 px-40 pb-0 pt-40"
      style={{
        background:
          'linear-gradient(to bottom, #000000 0%, #000000 70%, #FFFFFF 100%)',
      }}
      aria-labelledby="review-heading"
    >
      <div className="flex flex-col items-center gap-6 text-center">
        <p className="font-700 text-Gray-white typo-h5">
          웰로 기부자들의 찐 후기
        </p>
        <h2 id="review-heading" className="font-700 text-Gray-white typo-h5">
          고향사랑기부를 추천합니다!
        </h2>
      </div>

      <div
        className="relative h-240 w-full max-w-[375px] overflow-hidden"
        role="region"
        aria-label="기부자 후기 캐러셀"
        aria-live="polite"
        aria-atomic="false"
      >
        <div className="sr-only" role="status" aria-live="polite">
          {currentReview.text}, {currentReview.author}
        </div>
        <motion.div
          key={`carousel-${currentIndex}`}
          initial={{ y: 0 }}
          animate={{ y: -132 }}
          transition={{
            duration: ANIMATION_CONFIG.transitionDurationSeconds,
            ease: ANIMATION_CONFIG.easingCurve,
          }}
          className="flex w-full flex-col items-start gap-20 px-20"
          style={{
            WebkitMask:
              'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
            mask: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
          }}
        >
          <ReviewCard
            text={currentReview.text}
            author={currentReview.author}
            opacity={1}
            animateOpacity={0}
            isActive={true}
          />
          <ReviewCard
            text={nextReview.text}
            author={nextReview.author}
            opacity={0.3}
            animateOpacity={1}
            isActive={false}
          />
          <ReviewCard
            text={thirdReview.text}
            author={thirdReview.author}
            opacity={0.3}
            animateOpacity={0.3}
            isActive={false}
          />
        </motion.div>
      </div>
    </section>
  );
};

type ReviewCardProps = {
  text: string;
  author: string;
  opacity?: number;
  animateOpacity?: number;
  isActive?: boolean;
};

const ReviewCard = ({
  text,
  author,
  opacity = 1,
  animateOpacity = 1,
  isActive = false,
}: ReviewCardProps) => {
  return (
    <motion.article
      initial={{ opacity }}
      animate={{ opacity: animateOpacity }}
      transition={{
        duration: ANIMATION_CONFIG.transitionDurationSeconds,
        ease: 'easeOut',
      }}
      className="flex w-full flex-col items-center gap-10 rounded-b-[60px] rounded-tl-[60px] bg-Gray-white px-20 py-16"
      aria-hidden={!isActive}
    >
      <SvgStars />
      <div className="flex flex-col items-center gap-4 text-center">
        <p className="break-keep font-700 text-Gray-900 typo-body3">
          &quot;{text}&quot;
        </p>
        <p className="font-400 text-Gray-500 typo-caption1">{author}</p>
      </div>
    </motion.article>
  );
};

const SvgStars = () => {
  return (
    <svg
      width="100"
      height="20"
      viewBox="0 0 100 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="별점 5점 만점"
      role="img"
    >
      <path
        d="M10 0L12.25 6.91H19.51L13.63 11.18L15.88 18.09L10 13.82L4.12 18.09L6.37 11.18L0.49 6.91H7.75L10 0Z"
        fill="#FFD233"
      />
      <path
        d="M30 0L32.25 6.91H39.51L33.63 11.18L35.88 18.09L30 13.82L24.12 18.09L26.37 11.18L20.49 6.91H27.75L30 0Z"
        fill="#FFD233"
      />
      <path
        d="M50 0L52.25 6.91H59.51L53.63 11.18L55.88 18.09L50 13.82L44.12 18.09L46.37 11.18L40.49 6.91H47.75L50 0Z"
        fill="#FFD233"
      />
      <path
        d="M70 0L72.25 6.91H79.51L73.63 11.18L75.88 18.09L70 13.82L64.12 18.09L66.37 11.18L60.49 6.91H67.75L70 0Z"
        fill="#FFD233"
      />
      <path
        d="M90 0L92.25 6.91H99.51L93.63 11.18L95.88 18.09L90 13.82L84.12 18.09L86.37 11.18L80.49 6.91H87.75L90 0Z"
        fill="#FFD233"
      />
    </svg>
  );
};
